<template>
  <section
    class="bg-white lg:pl-0 px-6 lg:px-[2.142857rem] lg:py-[4rem] pb-[2rem] lg:scroll-mt-33 scroll-mt-63"
    id="Aisummary-comparsion"
    data-testid="po-section"
  >
    <div class="flex items-center gap-6 w-full">
      <div class="flex items-start gap-[16px]">
        <img
          :src="taiasmartSearch_icon"
          alt="Taia Chat logo"
          class="w-[32px] h-[32px]"
          data-testid="po-logo"
        />

        <div class="flex flex-col">
          <h2
            class="text-[#191919] text-[24px] font-normal leading-[21.6px] font-[Visuelt Pro]"
            data-testid="po-header-title"
          >
            Summary & Comparison
          </h2>
          <p
            class="text-[#555555] text-[12px] font-normal leading-[22px] tracking-[0.3px] font-[Visuelt Pro]"
            data-testid="po-header-subtitle"
          >
            Have questions? Taia can help you decide.
          </p>
        </div>
      </div>
      <TaiaLoading v-if="loading"></TaiaLoading>
    </div>

    <div
      role="tablist"
      aria-label="Product Information"
      class="flex gap-[32px] border-b border-[#ebebeb] mt-[24px] lg:mt-4"
      data-testid="po-tab-buttons"
    >
      <TaiaTabButton
        v-if="isOverviewVisible"
        id="product-overview-tab"
        label="Product Overview"
        :active="activeTab === 'overview'"
        aria-controls="product-overview-panel"
        @click="
          () => {
            activeTab = 'overview';
            trackTabEvent('click_summary_tab');
          }
        "
      />

      <TaiaTabButton
        v-if="isCompareVisible"
        id="compare-products-tab"
        label="Compare Products"
        :active="activeTab === 'compare'"
        aria-controls="compare-products-panel"
        @click="
          () => {
            activeTab = 'compare';
            trackTabEvent('click_compare_tab');
          }
        "
      />
    </div>

    <div class="font-sans overflow-y-auto">
      <div
        v-if="isOverviewVisible"
        v-show="activeTab === 'overview'"
        id="product-overview-panel"
        role="tabpanel"
        aria-labelledby="product-overview-tab"
        class="pt-[24px] lg:pt-[34px] pb-[30px] lg:pb-[24px]"
        data-testid="po-overview-content"
      >
        <div
          class="grid md:grid-cols-[minmax(0,1fr)_250px] gap-x-[24px] lg:relative"
          data-testid="po-overview-grid"
        >
          <div class="min-w-0">
            <div
              ref="collapseBox"
              id="po-overview-collapse"
              tabindex="-1"
              aria-live="polite"
              aria-atomic="false"
              :style="{ height: currentHeight + 'px' }"
              class="overflow-hidden transition-full duration-300 ease-in-out pl-[22px] text-[14px] text-[#555] leading-[22px] productoverviewtext"
            >
              <div
                v-html="overviewHtml"
                data-testid="po-overview-html-text"
                class="prose prose-sm max-w-none prose-p:mb-2 prose-ul:mb-2 prose-ol:mb-2 prose-li:mb-2 prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 prose-a:text-blue-600 prose-a:underline prose-li:marker:text-blue-500"
              />
            </div>
            <button
              v-if="hasOverflow"
              @click="toggleCollapse"
              type="button"
              :aria-expanded="!isCollapsed || showMobileSheet"
              aria-controls="po-overview-collapse"
              class="flex lg:absolute lg:bottom-[-4px] items-center gap-[8px] text-[14px] text-[#555] font-medium bg-white cursor-pointer px-[16px] py-[12px] pt-[21px] lg:pt-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#005FCC] rounded-sm"
              data-testid="po-read-more-button"
            >
              <span>
                {{ isCollapsed ? "Read more" : "Read less" }}
              </span>

              <i
                :class="[
                  'fa transition-transform duration-200',
                  isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up',
                ]"
                aria-hidden="true"
                data-testid="po-read-more-icon"
              />
            </button>
          </div>
          <div class="pt-[24px] md:pt-0">
            <div
              v-if="suggestions.length"
              class="flex flex-wrap gap-[8px]"
              data-testid="po-suggestions"
            >
              <TaiaPillButton
                v-for="(pill, i) in suggestions"
                :key="i"
                :label="pill"
                @click="onPromptClick(pill)"
              />
            </div>
          </div>
          <div
            class="flex items-center gap-[16px] mt-[48px] lg:mt-[24px] md:col-start-1 lg:ml-[133px]"
            data-testid="po-overview-actions"
          >
            <button
              @click.prevent="handleAskTaia"
              class="inline-flex items-center justify-center bg-white p-0 rounded cursor-pointer"
              data-testid="po-ask-taia-button"
            >
              <img
                :src="askTaiaImage"
                alt="Ask Taia"
                class="object-contain w-[100px] h-[36px]"
                data-testid="po-ask-taia-image"
              />
            </button>
          </div>
        </div>
      </div>
      <div
        v-if="isCompareVisible"
        v-show="activeTab === 'compare'"
        id="compare-products-panel"
        role="tabpanel"
        aria-labelledby="compare-products-tab"
        class="pt-[24px] pb-12"
        data-testid="po-compare-content"
      >
        <p
          class="text-[14px] text-[#555] leading-[22px] mb-[14px] lg:mb-[20px] max-w-[978px]"
          data-testid="po-compare-subtitle"
        >
          Here’s a comparison between this Thorne product and similar ones:
        </p>
        <div
          :class="[
            'w-full overflow-x-auto md:overflow-visible scrollbar-thin-x comparison-table-container',
            { 'comparison-table-container--alt-rows': true },
          ]"
          data-testid="po-compare-grid-container"
        >
          <div
            v-html="comparisonHtml"
            class="w-full [&_table]:w-full [&_th]:text-left [&_th]:text-[14px] [&_th]:font-medium [&_th]:text-[#191919] [&_th]:border-b [&_th]:border-[#ebebeb] [&_th]:py-[5px] [&_td]:text-[14px] [&_td]:text-[#555555] [&_td]:border-b [&_td]:border-[#ebebeb] [&_td]:py-[10px] [&_td:first-child]:w-[180px] md:[&_td:first-child]:w-[176px] [&_a]:text-blue-600 [&_a]:underline prose-a:text-blue-600 prose-li:marker:text-blue-500"
            data-testid="po-compare-html-content"
          />
        </div>
      </div>
    </div>

    <TaiaReadMoreSlider
      v-model="showMobileSheet"
      :title="`More about ${product.name}`"
      isPage="taiaProductOverview"
      :productData="product"
    >
      <div
        v-html="overviewHtml"
        class="prose prose-sm max-w-none"
        data-testid="po-overview-read-more-content"
      />
    </TaiaReadMoreSlider>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, watchEffect, onMounted, nextTick } from "vue";
import { storeToRefs } from "pinia";
import TaiaTabButton from "~/components/ui/atom/taiaTabButton.vue";
import TaiaPillButton from "~/components/ui/atom/taiaPillButton.vue";
import TaiaLoading from "~/components/ui/atom/taiaLoading.vue";
import { useIframeStore } from "@/stores/chatAIStore";
import { useUserStore } from "@/stores/userStore";
import eventBusGtm from "@/composables/eventBusGtm";
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import TaiaReadMoreSlider from "~/components/ui/organism/product/taiaReadMoreSlider.vue";
import taiasmartSearch_icon from "~/assets/tgpt/images/taiasmartSearch_icon.svg";
import askTaiaImage from "~/assets/tgpt/images/ask-taia-icon.svg";

const iframeStore = useIframeStore();
const userStore = useUserStore();
const { isDynamicYieldVariationData } =
  storeToRefs(iframeStore);
const props = defineProps<{
  compareData: any | null;
  overviewData: any | null;
  loading: boolean;
  defaultTab: any | null;
  isOverviewVisible: boolean;
  isCompareVisible: boolean;
  product: any | null;
}>();

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");

const showMobileSheet = ref(false);
const activeTab = ref<"overview" | "compare" | null>(null);

watchEffect(() => {
  if (props.loading) return;

  if (props.defaultTab) {
    activeTab.value = props.defaultTab;
    return;
  }

  if (!activeTab.value) {
    if (props.overviewData) activeTab.value = "overview";
    else if (props.compareData) activeTab.value = "compare";
  }
});
watch(activeTab, () => {
  setTimeout(recalcCollapse, 50);
});

const overviewHtml = computed(() => props.overviewData?.product_overview ?? "");

const comparisonHtml = computed(() => props.compareData?.comparison ?? "");

const suggestions = computed(() => props.overviewData?.suggestions ?? []);

const collapseBox = ref<HTMLElement | null>(null);

const isCollapsed = ref(true);
const hasOverflow = ref(false);
const collapsedHeight = 150;
const fullHeight = ref(0);
const currentHeight = ref(collapsedHeight);

const recalcCollapse = async () => {
  await nextTick();
  requestAnimationFrame(() => {
    const el = collapseBox.value;
    if (!el) return;

    el.style.maxHeight = "none";

    fullHeight.value = el.scrollHeight;

    if (fullHeight.value > collapsedHeight + 2) {
      hasOverflow.value = true;
      currentHeight.value = collapsedHeight;
      isCollapsed.value = true;
    } else {
      hasOverflow.value = false;
      currentHeight.value = fullHeight.value;
    }

    el.style.maxHeight = `${currentHeight.value}px`;
  });
};

watch(overviewHtml, async () => {
  await nextTick();
  recalcCollapse();
});

onMounted(() => {
  recalcCollapse();
});
const toggleCollapse = async () => {
  if (isMobile.value) {
    trackCollapseEvent();
    showMobileSheet.value = true;
    return;
  }

  const el = collapseBox.value;
  if (!el) return;

  const wasCollapsed = isCollapsed.value;

  // Expand / Collapse height
  if (wasCollapsed) {
    currentHeight.value = fullHeight.value;
  } else {
    currentHeight.value = collapsedHeight;
  }

  el.style.maxHeight = `${currentHeight.value}px`;

  // Toggle state
  isCollapsed.value = !isCollapsed.value;

  // KEY FIX: Focus ONLY last revealed element
  if (wasCollapsed) {
    await nextTick();

    requestAnimationFrame(() => {
      // Try targeting last list item first
      const lastListItem = el.querySelector("li:last-child");

      // Fallback to last paragraph
      const lastParagraph = el.querySelector("p:last-child");

      const focusTarget =
        lastListItem instanceof HTMLElement
          ? lastListItem
          : lastParagraph instanceof HTMLElement
            ? lastParagraph
            : null;

      if (focusTarget) {
        // Make focusable temporarily
        focusTarget.setAttribute("tabindex", "-1");

        // Focus it → screen reader reads ONLY this portion
        focusTarget.focus();
      }
    });
  }

  trackCollapseEvent();
};
const handleAskTaia = () => {
  iframeStore.sendMessageToChatbot("queryChat", {
    currentMinimise: false,
  });
  handleAnalyticsEvents();
};

const handleAnalyticsEvents = () => {
  const screenName = document.title;
  const customerId = userStore.data?.customerId ?? null;

  eventBusGtm.emit("trackEvent", {
    event: "Taia",
    button_name: "click_into_taia_pdpsummary",
    screen_name: screenName,
    Customer_id: customerId,
    message_id: null,
    Selected_thread_id: null,
    target: null,
    productName: props.product.name,
    productSku: props.product.sku,
    productPrice: props.product.price,
    skuid: null,
    sub_screen: "taia_summary_product_overview",
    article: null,
    Writing_style_preference: null,
    Content_preference: null,
    ResponseDetail_preference: null,
    Taia_campaign: null,
    search_term: null,
    variationName: isDynamicYieldVariationData.value?.name ?? null,
    experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
  });
};

const trackCollapseEvent = () => {
  let screenName = document.title;
  const customerId = userStore.data?.customerId ?? null;
  let eventName = !isCollapsed.value ? "readmore" : "readless";
  if (isMobile.value) {
    eventName = "readmore";
  }
  eventBusGtm.emit("trackEvent", {
    event: "Taia",
    button_name: eventName,
    screen_name: screenName,
    Customer_id: customerId,
    sub_screen: "taia_summary_product_overview",
    message_id: null,
    Selected_thread_id: null,
    target: null,
    productName: props.product.name,
    productSku: props.product.sku,
    productPrice: props.product.price,
    skuid: null,
    article: null,
    Writing_style_preference: null,
    Content_preference: null,
    ResponseDetail_preference: null,
    Taia_campaign: null,
    search_term: null,
    variationName: isDynamicYieldVariationData.value?.name ?? null,
    experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
  });
};

const trackTabEvent = (eventName: string) => {
  const screenName = document.title;
  const customerId = userStore.data?.customerId ?? null;
  const subScreenMap = {
    click_summary_tab: "taia_summary_product_overview",
    click_compare_tab: "taia_summary_product_overview",
  };
  eventBusGtm.emit("trackEvent", {
    event: "Taia",
    button_name: eventName,
    screen_name: screenName,
    Customer_id: customerId,
    message_id: null,
    Selected_thread_id: null,
    target: null,
    productName: props.product.name,
    productSku: props.product.sku,
    productPrice: props.product.price,
    skuid: null,
    sub_screen: subScreenMap[eventName] || null,
    article: null,
    Writing_style_preference: null,
    Content_preference: null,
    ResponseDetail_preference: null,
    Taia_campaign: null,
    search_term: null,
    variationName: isDynamicYieldVariationData.value?.name ?? null,
    experienceName: isDynamicYieldVariationData.value?.experienceName ?? null,
  });
};

const onPromptClick = (selectedPill: string) => {
  iframeStore.sendMessageToChatbot("pillSubmission", {
    currentMinimise: false,
    searchQuery: selectedPill,
    actionType: "pdpsummary",
  });
};
</script>
<style>
.productoverviewtext {
  .prose p {
    color: #555555;
  }
  ul {
    list-style: inherit;
  }
}
tr td:first-child {
  color: #191919;
  font-weight: 400;
}
tr:last-child td {
  border-bottom: unset;
}

th {
  padding-top: 10px;
  padding-right: 5px;
}

td {
  padding-right: 14px;
  padding-bottom: 22px;
  align-content: baseline;
}

.comparison-table-container table th:first-child,
.comparison-table-container table td:first-child {
  position: sticky;
  left: 0;
  background: white;
  z-index: 2;
}

.comparison-table-container table th:first-child {
  z-index: 3;
}
.comparison-table-container--alt-rows table tr:nth-child(odd) td {
  background-color: #fbfbfb !important;
}

.comparison-table-container--alt-rows table tr:nth-child(even) td {
  background-color: #ffffff !important;
}
.comparison-table-container--alt-rows table tr:nth-child(odd) td:first-child {
  background-color: #fbfbfb !important;
}

.comparison-table-container--alt-rows table tr:nth-child(even) td:first-child {
  background-color: #ffffff !important;
}
@media (max-width: 768px) {
  .comparison-table-container table th,
  .comparison-table-container table td {
    padding-top: 10px;
    padding-right: 5px;
    white-space: normal;
    vertical-align: top;
  }

  .comparison-table-container table td:first-child {
    min-width: 160px;
    white-space: normal;
  }

  .comparison-table-container table td {
    min-width: 140px;
  }

  .comparison-table-container table tr {
    border-bottom: 1px solid #ebebeb;
  }
}

.scrollbar-thin-x::-webkit-scrollbar {
  height: 10px;
}

.scrollbar-thin-x::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.scrollbar-thin-x::-webkit-scrollbar-thumb {
  background-color: #999999;
  border-radius: 4px;
  border: 2px solid #f1f1f1;
  background-clip: content-box;
}
</style>
